/**
 * CR164: Locator Results Panel
 * Displays deal locator regions with matched keywords, coords, and rule satisfaction
 */

import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { 
  Target, MapPin, CheckCircle, XCircle, 
  Zap, Eye, Clock, Grid3X3, Play
} from 'lucide-react';
import type { 
  PageLocatorResults, 
  LocatorRegion, 
  TightMatchRule 
} from '@/lib/flyer-pipeline-v2/dealLocatorTypes';

interface LocatorResultsPanelProps {
  results: PageLocatorResults | null;
  /** Callback when user wants to run AI on a specific crop */
  onRunAiOnCrop?: (region: LocatorRegion) => void;
  /** Whether overlay mode is supported (image viewer) */
  supportsOverlay?: boolean;
  /** Current overlay visibility state */
  showOverlay?: boolean;
  /** Toggle overlay visibility */
  onToggleOverlay?: (show: boolean) => void;
}

function getRuleBadge(rule: TightMatchRule): { label: string; variant: 'default' | 'secondary' | 'outline' } {
  switch (rule) {
    case 'GIFT_INDICATOR_DIRECT':
      return { label: 'Gift indicator', variant: 'default' };
    case 'CONTEXT_WINDOW_MATCH':
      return { label: 'Context window', variant: 'secondary' };
    case 'MONEY_CUE_FOUND':
      return { label: 'Money cue', variant: 'secondary' };
    case 'NO_RULE_REQUIRED':
      return { label: 'No rule', variant: 'outline' };
    default:
      return { label: rule, variant: 'outline' };
  }
}

export function LocatorResultsPanel({
  results,
  onRunAiOnCrop,
  supportsOverlay = false,
  showOverlay = false,
  onToggleOverlay,
}: LocatorResultsPanelProps) {
  const [expandedRegion, setExpandedRegion] = useState<string | null>(null);

  if (!results) {
    return (
      <div className="p-4 border rounded-lg bg-muted/20 text-center text-muted-foreground">
        <Target className="h-6 w-6 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No locator results available</p>
        <p className="text-xs mt-1">Run the Deal Locator to detect regions</p>
      </div>
    );
  }

  if (!results.locatorRan) {
    return (
      <div className="p-4 border rounded-lg bg-amber-50 dark:bg-amber-900/20">
        <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
          <XCircle className="h-4 w-4" />
          <span className="text-sm font-medium">Locator was skipped</span>
        </div>
        {results.skipReason && (
          <p className="text-xs text-muted-foreground mt-1">{results.skipReason}</p>
        )}
      </div>
    );
  }

  const hasRegions = results.regions.length > 0;

  return (
    <div className="space-y-4">
      {/* Summary header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4" />
          <span className="text-sm font-medium">Locator Results</span>
          <Badge 
            variant={hasRegions ? "default" : "secondary"}
            className="text-xs"
          >
            {results.regions.length} region{results.regions.length !== 1 ? 's' : ''}
          </Badge>
        </div>

        <div className="flex items-center gap-3">
          {results.tiledScanningUsed && (
            <Badge variant="outline" className="text-xs">
              <Grid3X3 className="h-3 w-3 mr-1" />
              {results.tileCount} tiles
            </Badge>
          )}
          <Badge variant="outline" className="text-xs">
            <Clock className="h-3 w-3 mr-1" />
            {results.durationMs}ms
          </Badge>
        </div>
      </div>

      {/* Overlay toggle (if supported) */}
      {supportsOverlay && hasRegions && (
        <div className="flex items-center justify-between py-2 px-3 border rounded bg-muted/30">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-muted-foreground" />
            <Label className="text-xs">Show region overlay on image</Label>
          </div>
          <Switch
            checked={showOverlay}
            onCheckedChange={onToggleOverlay}
          />
        </div>
      )}

      {/* AI used crops indicator */}
      {results.aiUsedCrops && (
        <div className="p-3 border rounded-lg bg-green-50 dark:bg-green-900/20">
          <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
            <Zap className="h-4 w-4" />
            <span className="text-sm font-medium">AI used locator crops</span>
          </div>
          {results.cropArtifactPaths && results.cropArtifactPaths.length > 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              {results.cropArtifactPaths.length} crop{results.cropArtifactPaths.length !== 1 ? 's' : ''} sent to AI
            </p>
          )}
        </div>
      )}

      {/* Regions list */}
      {hasRegions ? (
        <ScrollArea className="h-64 border rounded-lg">
          <div className="p-2 space-y-2">
            {results.regions.map((region, i) => {
              const isExpanded = expandedRegion === region.id;
              const ruleBadge = getRuleBadge(region.ruleSatisfied);

              return (
                <div 
                  key={region.id}
                  className="border rounded bg-background"
                >
                  <button
                    className="w-full px-3 py-2 flex items-start justify-between text-left hover:bg-muted/50 transition-colors"
                    onClick={() => setExpandedRegion(isExpanded ? null : region.id)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <MapPin className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                        <span className="text-xs font-medium">
                          Region {i + 1}
                        </span>
                        {region.matchedKeywords.map((kw, j) => (
                          <Badge key={j} variant="secondary" className="text-xs">
                            {kw}
                          </Badge>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 truncate">
                        {region.snippet}
                      </p>
                    </div>
                    <Badge variant={ruleBadge.variant} className="text-xs flex-shrink-0 ml-2">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      {ruleBadge.label}
                    </Badge>
                  </button>

                  {isExpanded && (
                    <div className="px-3 pb-3 border-t bg-muted/20 space-y-3">
                      {/* Coordinates */}
                      <div className="grid grid-cols-4 gap-2 py-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">X: </span>
                          <span className="font-mono">{region.coords.x}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Y: </span>
                          <span className="font-mono">{region.coords.y}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">W: </span>
                          <span className="font-mono">{region.coords.width}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">H: </span>
                          <span className="font-mono">{region.coords.height}</span>
                        </div>
                      </div>

                      {/* Rule satisfaction details */}
                      <div className="text-xs">
                        <span className="text-muted-foreground">Rule Satisfied: </span>
                        <span className="font-medium">{region.ruleSatisfied}</span>
                        {region.ruleDetails && (
                          <p className="text-muted-foreground mt-1">{region.ruleDetails}</p>
                        )}
                      </div>

                      {/* Score and tile info */}
                      <div className="flex items-center gap-3 text-xs">
                        <span>
                          <span className="text-muted-foreground">Score: </span>
                          <span className="font-medium">{region.score.toFixed(2)}</span>
                        </span>
                        {region.tileIndex !== undefined && (
                          <span>
                            <span className="text-muted-foreground">Tile: </span>
                            <span className="font-mono">{region.tileIndex + 1}</span>
                          </span>
                        )}
                      </div>

                      {/* Full snippet */}
                      <div className="p-2 border rounded bg-background text-xs font-mono whitespace-pre-wrap">
                        {region.snippet}
                      </div>

                      {/* Run AI on crop action */}
                      {onRunAiOnCrop && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onRunAiOnCrop(region)}
                          className="w-full text-xs"
                        >
                          <Play className="h-3 w-3 mr-1" />
                          Run AI on this crop
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>
      ) : (
        <div className="p-4 border rounded-lg bg-muted/20 text-center">
          <Target className="h-6 w-6 mx-auto mb-2 text-muted-foreground opacity-50" />
          <p className="text-sm text-muted-foreground">No regions detected</p>
          <p className="text-xs text-muted-foreground mt-1">
            No matching keywords found in page OCR
          </p>
        </div>
      )}
    </div>
  );
}
